import React, { useMemo, useState } from 'react';

import { ButtonBase, createStyles, InputBase, makeStyles, Theme } from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';

import { InviteItem, InviteListItem } from '../../../types';
import { monochrome, storiesDarkModeColor } from '../../../theme/styles';
import { groupInvitePlaces, InvitePlace, InviteTone } from '../config';

interface Props {
  invite: InviteListItem;
  /** Absent where there is no retry route (Legend) or no right to retry */
  onRetry?: (invite: InviteListItem, item: InviteItem) => void;
  retryingItemIds: Set<string>;
}

type GroupKey = 'teams' | 'locations' | 'paySchedules';

const GROUPS: { key: GroupKey; label: string }[] = [
  { key: 'teams', label: 'Teams' },
  { key: 'locations', label: 'Locations' },
  { key: 'paySchedules', label: 'Pay schedules' },
];

// Past this many a list gets a search box
const SEARCH_FROM = 8;

// The onboarding card is dark in both themes, so this uses the dark-mode tokens throughout
const TONE_COLORS: Record<InviteTone, string> = {
  success: storiesDarkModeColor.success,
  error: storiesDarkModeColor.error,
  warning: storiesDarkModeColor.warning,
  neutral: monochrome.lightest,
  muted: monochrome.medium,
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    title: {
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: 0.6,
      textTransform: 'uppercase',
      color: monochrome.medium,
      marginBottom: theme.spacing(1),
    },
    row: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: 12,
      padding: '3px 0',
    },
    key: {
      flexShrink: 0,
      width: 96,
      fontSize: 12,
      lineHeight: '20px',
      color: monochrome.medium,
    },
    value: {
      flex: 1,
      minWidth: 0,
    },
    first: {
      display: 'flex',
      alignItems: 'center',
      gap: 6,
    },
    name: {
      fontSize: 12,
      lineHeight: '20px',
      fontWeight: 600,
      color: monochrome.lightest,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      minWidth: 0,
    },
    tag: {
      flexShrink: 0,
      fontSize: 10,
      fontWeight: 700,
      lineHeight: '16px',
      padding: '0 6px',
      borderRadius: 4,
      color: monochrome.lightest,
      backgroundColor: alpha(monochrome.lightest, 0.14),
    },
    count: {
      flexShrink: 0,
      fontSize: 11,
      fontWeight: 600,
      color: monochrome.medium,
    },
    more: {
      flexShrink: 0,
      fontSize: 11,
      fontWeight: 700,
      lineHeight: '18px',
      padding: '0 6px',
      borderRadius: 9,
      color: storiesDarkModeColor.primary,
      backgroundColor: alpha(storiesDarkModeColor.primary, 0.16),
    },
    link: {
      fontSize: 11,
      fontWeight: 700,
      color: storiesDarkModeColor.primary,
      '&:disabled': {
        color: monochrome.medium,
      },
    },
    issue: {
      display: 'flex',
      alignItems: 'baseline',
      flexWrap: 'wrap',
      gap: '0 8px',
      fontSize: 11,
      lineHeight: '16px',
      fontWeight: 600,
    },
    openHead: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginTop: 4,
    },
    search: {
      width: '100%',
      margin: '6px 0',
      padding: '2px 8px',
      borderRadius: 6,
      fontSize: 12,
      color: monochrome.lightest,
      backgroundColor: alpha(monochrome.lightest, 0.08),
    },
    list: {
      maxHeight: 200,
      overflowY: 'auto',
      marginTop: 4,
    },
    listItem: {
      padding: '4px 0',
      borderTop: `1px solid ${alpha(monochrome.lightest, 0.08)}`,
      '&:first-child': {
        borderTop: 'none',
      },
    },
    none: {
      fontSize: 12,
      color: monochrome.medium,
      fontStyle: 'italic',
    },
  })
);

const PlaceLine = ({
  place,
  invite,
  onRetry,
  retryingItemIds,
  after,
}: Omit<Props, 'invite'> & { invite: InviteListItem; place: InvitePlace; after?: React.ReactNode }) => {
  const classes = useStyles();
  const retrying = place.retryable.some(item => retryingItemIds.has(item.uuid));
  const canRetry = !!onRetry && place.retryable.length > 0;

  return (
    <>
      <span className={classes.first}>
        <span className={classes.name} title={place.name}>
          {place.name}
        </span>
        {place.payScheme && <span className={classes.tag}>{place.payScheme}</span>}
        {place.shiftCount > 1 && <span className={classes.count}>{place.shiftCount} shifts</span>}
        {after}
      </span>
      {place.issue && (
        <span className={classes.issue} style={{ color: TONE_COLORS[place.issue.tone] }}>
          {place.issue.label}
          {canRetry && (
            <ButtonBase
              className={classes.link}
              disabled={retrying}
              onClick={() => place.retryable.forEach(item => onRetry?.(invite, item))}>
              {retrying ? 'Retrying…' : 'Retry'}
            </ButtonBase>
          )}
        </span>
      )}
    </>
  );
};

const PlaceGroup = ({
  label,
  places,
  open,
  onToggle,
  ...lineProps
}: Props & { label: string; places: InvitePlace[]; open: boolean; onToggle: () => void }) => {
  const classes = useStyles();
  const [query, setQuery] = useState('');

  const extra = places.length - 1;
  if (!open || extra === 0) {
    return (
      <div className={classes.row}>
        <span className={classes.key}>{label}</span>
        <span className={classes.value}>
          <PlaceLine
            {...lineProps}
            place={places[0]}
            after={
              extra > 0 && (
                <ButtonBase
                  className={classes.more}
                  onClick={onToggle}
                  aria-label={`Show all ${places.length}`}>
                  +{extra}
                </ButtonBase>
              )
            }
          />
        </span>
      </div>
    );
  }

  const needle = query.trim().toLowerCase();
  const shown = needle ? places.filter(place => place.name.toLowerCase().includes(needle)) : places;

  return (
    <div>
      <div className={classes.openHead}>
        <span className={classes.key}>
          {label} <span className={classes.count}>{places.length}</span>
        </span>
        <ButtonBase className={classes.link} onClick={onToggle}>
          Show less
        </ButtonBase>
      </div>
      {places.length > SEARCH_FROM && (
        <InputBase
          className={classes.search}
          autoFocus
          value={query}
          onChange={event => setQuery(event.target.value)}
          placeholder={`Filter ${places.length} ${label.toLowerCase()}…`}
          inputProps={{ 'aria-label': `Filter ${label.toLowerCase()}` }}
        />
      )}
      <div className={classes.list}>
        {shown.map(place => (
          <div key={place.key} className={classes.listItem}>
            <PlaceLine {...lineProps} place={place} />
          </div>
        ))}
        {!shown.length && <span className={classes.none}>No matches</span>}
      </div>
    </div>
  );
};

/**
 * "Invited to" block of the onboarding card: every team, location and pay schedule an invite puts
 * the person on, one line per kind — the first place and a "+N" that opens the rest in place.
 */
export const InviteDetails = ({ invite, onRetry, retryingItemIds }: Props) => {
  const classes = useStyles();
  const groups = useMemo(() => groupInvitePlaces(invite), [invite]);
  const [open, setOpen] = useState<GroupKey | null>(null);

  const present = GROUPS.filter(group => groups[group.key].length > 0);
  if (!present.length) {
    return null;
  }

  return (
    <div>
      <div className={classes.title}>Invited to</div>
      {present.map(group => (
        <PlaceGroup
          key={group.key}
          label={group.label}
          places={groups[group.key]}
          open={open === group.key}
          onToggle={() => setOpen(current => (current === group.key ? null : group.key))}
          invite={invite}
          onRetry={onRetry}
          retryingItemIds={retryingItemIds}
        />
      ))}
    </div>
  );
};
